import {
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  ValidationError,
  collection,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import { isNil } from "@mixitone/util";
import Club from "./Club";
import ClubDocument from "./ClubDocument";
import ClubPlayerAcceptedDocument from "./ClubPlayerAcceptedDocument";
import ClubPlayerMembership from "./ClubPlayerMembership";
import ClubPlayerToken from "./ClubPlayerToken";
import Group from "./Group";
import Note from "./Note";
import Player from "./Player";
import PlayerRating from "./PlayerRating";
import { acceptanceCoversDocument, documentRequiredBeforeMembership } from "./clubDocumentAccess";
import { setAccountId } from "./concerns/SetAccountId";
import { Database } from "./database.types";

export type ClubPlayerMemberType = Database["public"]["Enums"]["club_player_member_type"];
export type AcceptedDocument = { version: number; accepted_at: string };
export type AcceptedDocuments = Record<string, AcceptedDocument>;

function isOpenMembership(membership: ClubPlayerMembership): boolean {
  return !membership.is_deleted && isNil(membership.ended_at);
}

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    account_user_id: {
      type: "string",
    },
    active: {
      type: "boolean",
    },
    club_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    email: {
      type: "string",
    },
    graduation_level: {
      type: "number",
    },
    group_id: {
      type: "string",
    },
    invited_at: {
      type: "datetime",
    },
    is_deleted: {
      type: "boolean",
    },
    latest_rating: {
      type: "number",
    },
    latest_rating_date: {
      type: "string",
    },
    member_type: {
      type: "enum",
      enumType: "casual" as Database["public"]["Enums"]["club_player_member_type"],
    },
    name: {
      type: "string",
    },
    partner_id: {
      type: "string",
    },
    phone: {
      type: "string",
    },
    player_profile_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    user_id: {
      type: "string",
    },
  },
  {
    notes: collection(() => Note, "club_player_id", {
      order: "created_at",
    }),
    club_player_tokens: collection(() => ClubPlayerToken, "club_player_id"),
    ratings: collection(() => PlayerRating, "club_player_id"),
    players: collection(() => Player, "club_player_id"),
    accepted_documents: collection(() => ClubPlayerAcceptedDocument, "club_player_id"),
    memberships: collection(() => ClubPlayerMembership, "club_player_id", {
      order: "started_at",
    }),
  },
  {
    club: reference<any>(() => Club as any, "club_id"),
    group: reference<any>(() => Group as any, "group_id"),
  },
);

interface ClubPlayer extends ModelAttributes<typeof config> {
  get group(): Loadable<Group>;
  set group(value: Group | undefined);

  get club(): Loadable<Club>;
  set club(value: Club | undefined);
}

@setAccountId
@model("club_players", config)
class ClubPlayer extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description: `A club player is a player that is a member of a club. 
      They must be a member of a group and will be added to the first club group if none is specified. 
      Formal membership is stored in club_player_memberships (member_type column is legacy, unused by app logic).`,
    availableFields: [...Object.keys(config.fields), "firstSessionDate"],
    relationships: {
      club: "clubs where id equals this club_id",
      group: "groups where id equals this group_id",
      players: "players where club_player_id equals this id",
      nights:
        "nights (sessions) through players where players.night_id equals night.id and players.club_player_id equals this id",
    },
  };

  get noteCount() {
    if (this.notes.loaded) {
      return this.notes.length;
    } else {
      const notesData = this.data.get("notes");
      if (Array.isArray(notesData) && !isNil(notesData[0]?.count)) {
        return notesData[0].count as number;
      }
    }

    return 0;
  }

  /** Earliest session date from player attendance. Requires preloaded data or loaded players with nights. */
  get firstSessionDate(): Date | undefined {
    const preloaded = this.data.get("first_session_date");
    if (!isNil(preloaded) && preloaded !== "") {
      return preloaded instanceof Date ? preloaded : new Date(String(preloaded));
    }

    if (!this.players.loaded || this.players.length === 0) {
      return undefined;
    }

    let earliest: Date | undefined;
    for (const player of this.players) {
      if (player.is_deleted) continue;
      if (!player.night.loaded || player.night.is_deleted) continue;
      const nightDate = player.night.date;
      if (!nightDate) continue;
      const date = nightDate instanceof Date ? nightDate : new Date(nightDate);
      if (!earliest || date < earliest) {
        earliest = date;
      }
    }

    return earliest;
  }

  /** Open membership period, if any. Requires `memberships` to be loaded. */
  get currentMembership(): ClubPlayerMembership | undefined {
    if (!this.memberships.loaded) return undefined;
    return this.memberships.find(isOpenMembership);
  }

  /** Membership periods with ended dates, newest first. Requires `memberships` to be loaded. */
  get membershipHistory(): ClubPlayerMembership[] {
    if (!this.memberships.loaded) return [];
    return this.memberships
      .filter((m) => !m.is_deleted && !isNil(m.ended_at))
      .toSorted((a, b) => {
        const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
        const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
        return bTime - aTime;
      });
  }

  get membershipStartedAt(): Date | undefined {
    const started = this.currentMembership?.started_at;
    if (!started) return undefined;
    return started instanceof Date ? started : new Date(started);
  }

  /** Whether the player has an open membership period. Requires `memberships` to be loaded. */
  get isCurrentMember(): boolean {
    if (!this.memberships.loaded) return false;
    return Boolean(this.currentMembership);
  }

  /** Current member/casual status from membership periods. Requires `memberships` to be loaded. */
  get derivedMemberType(): ClubPlayerMemberType {
    return this.isCurrentMember ? "member" : "casual";
  }

  /** Loads membership periods, reloading when the collection was already fetched. */
  async ensureMembershipsLoaded(): Promise<void> {
    if (this.memberships.loaded) {
      await this.memberships.reload();
    } else {
      await this.memberships.load();
    }
  }

  private async reloadMemberships(): Promise<void> {
    if (this.memberships.loaded) {
      await this.memberships.reload();
    } else {
      await this.memberships.load();
    }
  }

  async startMembership(options?: { startedAt?: Date }): Promise<ClubPlayerMembership> {
    if (!this.persisted) {
      throw new ValidationError({ membership: "Player must be saved before starting membership" });
    }

    await this.reloadMemberships();
    if (this.currentMembership) {
      throw new ValidationError({ membership: "Player already has an open membership" });
    }

    const membership = new ClubPlayerMembership({
      account_id: this.account_id,
      club_id: this.club_id,
      club_player_id: this.id,
    });
    membership.writeAttribute("started_at", options?.startedAt ?? new Date());
    await membership.create();
    this.memberships.add(membership);

    return membership;
  }

  async endMembership(options?: { endedAt?: Date }): Promise<void> {
    await this.reloadMemberships();
    const current = this.currentMembership;
    if (!current) {
      throw new ValidationError({ membership: "Player has no open membership" });
    }

    current.ended_at = options?.endedAt ?? new Date();
    await current.save();
  }

  override async create() {
    if (isNil(this.name)) {
      throw new ValidationError({ name: "cannot be blank" });
    }

    if (isNil(this.group_id)) {
      await this.club.load();
      await this.club.ensureGroups();
      this.group_id = this.club.groups[0].id;
    }

    // New players always start at level 1.
    if (isNil(this.graduation_level)) {
      this.graduation_level = 1;
    }

    return super.create();
  }

  override async save() {
    // New players default to level 1.
    if (!this.persisted && isNil(this.graduation_level)) {
      this.graduation_level = 1;
    }

    // Any group change resets graduation level to 1.
    if (this.changedAttributes.has("group_id")) {
      this.graduation_level = 1;
    }

    return super.save();
  }

  get ratingOrDefault() {
    return this.latest_rating ?? 1500;
  }

  /** True when a live acceptance matches this document version. Requires `accepted_documents` loaded. */
  hasAcceptedDocumentVersion(document: {
    version?: number | null;
    series_id?: string | null;
    name?: string | null;
  }): boolean {
    return this.accepted_documents.some((acceptance) => acceptanceCoversDocument(acceptance, document));
  }

  async canBeMember(): Promise<[boolean, MemberReason[]]> {
    const reasons = [];
    if (!this.active) return [false, [{ message: "Player is not active", field: "active" }]];

    await this.club.load();
    if (this.club.club_documents.loaded) {
      await this.club.club_documents.reload();
    } else {
      await this.club.club_documents.load();
    }
    if (!this.accepted_documents.loaded) {
      await this.accepted_documents.load();
    }

    if (this.club.getMemberRequirement("email", false)) {
      if (isNil(this.email) || this.email.trim() === "")
        reasons.push({ message: "Email is required", field: "email" });
    }
    if (this.club.getMemberRequirement("phone", false)) {
      if (isNil(this.phone) || this.phone.trim() === "")
        reasons.push({ message: "Phone number is required", field: "phone" });
    }

    const latestDocuments = ClubDocument.onlyLastVersion(
      this.club.club_documents.filter((doc) => !doc.is_deleted),
    );
    for (const doc of latestDocuments) {
      if (!doc.name) continue;
      if (!documentRequiredBeforeMembership(doc) || this.hasAcceptedDocumentVersion(doc)) continue;
      reasons.push({ message: doc.name, field: "documents" });
    }

    if (reasons.length > 0) {
      return [false, reasons];
    } else {
      return [true, []];
    }
  }
}

interface MemberReason {
  message: string;
  field: string;
}

export default ClubPlayer;
